/**
 * Coin choice for crypto deposits: the asset from a searchable dropdown with icons ("Popular" first,
 * then "More coins"), then the network it is sent on from a second dropdown, with a fee hint.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cryptoCoin } from '@platform/shared';
import { cx } from '../../../components/ui';
import { AIcon } from '../kit';
import { COIN_MARKS } from './coinMarks';
import { ASSET_NAME, CoinIcon, feeNote, Mark, marksOf, POPULAR_ASSETS } from './coins';

export interface Option {
  id: string;
  icon: ReactNode;
  title: string;
  sub?: string;
  /** Shown on the right of the row ("3 networks"). */
  meta?: string;
  group?: string;
  /** Extra words the search matches (network names). */
  keywords?: string;
}

/** Dropdown with icons (a native select can't show them). Keyboard: arrows, Enter, Esc; type to search. */
export function Dropdown({ label, options, value, onChange, disabled }: { label: string; options: Option[]; value: string; onChange: (id: string) => void; disabled?: boolean }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const one = options.length < 2 || !!disabled;
  const searchable = options.length > 8;
  const needle = q.trim().toLowerCase();
  const shown = needle ? options.filter((o) => `${o.title} ${o.sub ?? ''} ${o.keywords ?? ''}`.toLowerCase().includes(needle)) : options;

  useEffect(() => {
    if (!open) return;
    setQ('');
    setHi(Math.max(0, options.findIndex((o) => o.id === value)));
    input.current?.focus();
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (open) list.current?.querySelector(`[data-i="${hi}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, hi]);

  const close = () => {
    setOpen(false);
    btn.current?.focus();
  };
  const pick = (v: string) => {
    onChange(v);
    close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return setOpen(true);
      if (shown.length) setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : shown.length - 1)) % shown.length);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (shown[hi]) pick(shown[hi].id);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') setOpen(false);
  };
  const row = (o: Option, big?: boolean) => (
    <>
      {o.icon}
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <b className={cx('font-semibold', big ? 'text-[16px]' : 'text-[15px]')}>{o.title}</b>
        {o.sub && <span className="truncate text-[12px] text-muted">{o.sub}</span>}
      </span>
    </>
  );
  const cur = options.find((o) => o.id === value);
  return (
    <div ref={box} className="relative flex min-w-0 flex-col gap-2.5">
      <span id={id} className="text-[14px] font-semibold">
        {label}
      </span>
      <button
        ref={btn}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={id}
        disabled={one}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKey}
        className={cx(
          'flex min-h-[64px] w-full items-center gap-3 rounded-[14px] border bg-track px-3.5 py-3 text-left disabled:cursor-default',
          open ? 'border-accent' : 'border-transparent enabled:hover:border-line-strong',
        )}
      >
        {cur ? row(cur, true) : <span className="flex-1 text-muted">Choose</span>}
        {!one && <AIcon name="right" size={18} className={cx('shrink-0 text-muted transition-transform', open ? '-rotate-90' : 'rotate-90')} />}
      </button>
      {open && (
        <div className="shadow-pop absolute inset-x-0 top-[calc(100%+6px)] z-20 overflow-hidden rounded-[16px] border border-line-strong bg-card">
          {searchable && (
            <div className="flex items-center gap-2 border-b border-line px-3.5 py-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden className="shrink-0 text-muted">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>
              <input
                ref={input}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setHi(0);
                }}
                onKeyDown={onKey}
                placeholder={`Search ${label.toLowerCase()}`}
                aria-label={`Search ${label.toLowerCase()}`}
                autoComplete="off"
                className="min-w-0 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-muted"
              />
            </div>
          )}
          <ul
            ref={list}
            role="listbox"
            aria-labelledby={id}
            className="m-0 max-h-[360px] list-none overflow-y-auto overscroll-contain p-1.5 [mask-image:linear-gradient(to_bottom,#000_calc(100%-24px),transparent)] pb-5"
          >
            {!shown.length && <li className="px-3 py-6 text-center text-[14px] text-muted">No coin matches “{q.trim()}”</li>}
            {shown.map((o, i) => (
              <li key={o.id} role="presentation">
                {o.group && o.group !== shown[i - 1]?.group && <div className="px-2.5 pt-2.5 pb-1 text-[11px] font-semibold tracking-[0.08em] text-muted uppercase">{o.group}</div>}
                <div
                  role="option"
                  data-i={i}
                  aria-selected={o.id === value}
                  onMouseMove={() => hi !== i && setHi(i)}
                  onClick={() => pick(o.id)}
                  className={cx('flex cursor-pointer items-center gap-3 rounded-[10px] px-2.5 py-2', i === hi && 'bg-track', o.id === value && 'bg-accent-bg')}
                >
                  {row(o)}
                  {o.meta && <span className="shrink-0 rounded-full bg-pill px-2 py-0.5 text-[11px] font-semibold text-muted">{o.meta}</span>}
                  {o.id === value && (
                    <span aria-hidden className="text-[14px] font-bold text-accent-fg">
                      ✓
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Asset first (USDT, ETH, BTC, XRP …), then the network that asset is sent on. `coins` arrive in page order. */
export function CoinPicker({ coins, value, onChange, className }: { coins: string[]; value: string; onChange: (code: string) => void; className?: string }) {
  const assets: string[] = [];
  const byAsset = new Map<string, string[]>();
  for (const c of coins) {
    const a = cryptoCoin(c).symbol;
    if (!byAsset.has(a)) {
      assets.push(a);
      byAsset.set(a, []);
    }
    byAsset.get(a)!.push(c);
  }
  const asset = value ? cryptoCoin(value).symbol : (assets[0] ?? '');
  const nets = byAsset.get(asset) ?? [];
  const grouped = assets.length > POPULAR_ASSETS + 2;
  return (
    <div className={cx('flex flex-col gap-6', className)}>
      <Dropdown
        label="Currency"
        value={asset}
        onChange={(a) => onChange(byAsset.get(a)?.[0] ?? '')}
        options={assets.map((a, i) => {
          const list = byAsset.get(a)!;
          return {
            id: a,
            icon: <CoinIcon code={list[0]!} size={30} badge={false} />,
            title: a,
            sub: ASSET_NAME[a],
            meta: list.length > 1 ? `${list.length} networks` : undefined,
            group: grouped ? (i < POPULAR_ASSETS ? 'Popular' : 'More coins') : undefined,
            keywords: list.map((c) => cryptoCoin(c).network).join(' '),
          };
        })}
      />
      {nets.length > 0 && (
        <Dropdown
          label="Network"
          value={value}
          onChange={onChange}
          options={nets.map((c, i) => {
            const m = marksOf(c);
            const net = m.net ?? m.coin;
            return {
              id: c,
              icon: net && COIN_MARKS[net] ? <Mark k={net} size={30} /> : <CoinIcon code={c} size={30} badge={false} />,
              title: cryptoCoin(c).network || cryptoCoin(c).symbol,
              sub: nets.length > 1 ? (feeNote(c) ?? undefined) : undefined,
              meta: nets.length > 1 && i === 0 ? 'Default' : undefined,
            };
          })}
        />
      )}
    </div>
  );
}
